import React, { Dispatch, MouseEventHandler, SetStateAction } from 'react';

import { useDispatch } from 'react-redux';

import TranslationHelper from '../../../../../helpers/TranslationHelper';
import { useDebounceEffect } from '../../../../../helpers/hooks';

import { DEBOUNCE_LOW } from '../../../../../constants/DebounceValues';

import { IListObjectInitialState } from '../../../../../components/TransferList/CommonTransferList';
import {
    clearEmptyAssignments,
    fetchProductEmptyAssignments,
} from '../../../state';
import {
    useProductEmptyAssignmentsStatus,
    useSelectedProduct,
} from '../../../state/index.hooks';
import useAsyncThunkWithSnackbar from '../../../../../hooks/useAsyncThunkWithSnackbar';
import ProductCustomList from '../CustomList/ProductCustomList';
import { IProductAssignment, IProductFilterInitialState } from '../../../types';

interface IOwnProps {
    objects: IListObjectInitialState<IProductAssignment>;
    checked: IProductAssignment[];
    setChecked: Dispatch<SetStateAction<IProductAssignment[]>>;
    filter: IProductFilterInitialState;
    setFilter: React.Dispatch<Partial<IProductFilterInitialState>>;
    handleToggle: (value: IProductAssignment) => MouseEventHandler;
}

const NAME_FILTER_MIN_LENGTH = 3;

const LeftList = ({
    filter,
    setFilter,
    objects,
    checked,
    setChecked,
    handleToggle,
}: IOwnProps) => {
    const dispatch = useDispatch();

    const productEmptyAssignmentsStatus = useProductEmptyAssignmentsStatus();
    const selectedProduct = useSelectedProduct();
    const handleAsyncRequest = useAsyncThunkWithSnackbar();

    const { nameUnassigned } = filter;
    const { unassignedObjects } = objects;
    useDebounceEffect(
        () => {
            handleChangeNameUnassigned();
        },
        [nameUnassigned],
        DEBOUNCE_LOW
    );

    const unassignedTitle = TranslationHelper.translate('objects.unassigned');

    const validName =
        nameUnassigned.includes('*') ||
        nameUnassigned.length >= NAME_FILTER_MIN_LENGTH;

    const prepareFilterParams = () => {
        const filter: { name: string; excludedProductId: number } = {
            name: nameUnassigned,
            excludedProductId: selectedProduct?.id ?? -1,
        };
        return filter;
    };

    const handleChangeNameUnassigned = () => {
        if (!nameUnassigned) {
            dispatch(clearEmptyAssignments());
            return;
        }
        if (validName) {
            handleAsyncRequest({
                asyncAction: fetchProductEmptyAssignments({
                    queryParams: prepareFilterParams(),
                }),
            });
        } else {
            dispatch(clearEmptyAssignments());
        }
    };

    return (
        <ProductCustomList
            loaded={productEmptyAssignmentsStatus !== 'loading'}
            nameInput={{
                name: nameUnassigned,
                setName: (name) => setFilter({ nameUnassigned: name }),
            }}
            title={unassignedTitle}
            items={unassignedObjects}
            handleToggle={handleToggle}
            checked={checked}
            setChecked={setChecked}
            tooltipTitle="To search for unassigned objects, you have to enter at least 3 characters or use * as a wildcard."
        />
    );
};

export default LeftList;
